import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import { SourceMetric } from '../../types';

interface SourceBreakdownProps {
  data: SourceMetric[];
  loading: boolean;
}

const COLORS = [
  'hsl(217, 91%, 60%)',  // blue
  'hsl(142, 71%, 45%)',  // green
  'hsl(262, 83%, 58%)',  // purple
  'hsl(24, 95%, 53%)',   // orange
  'hsl(348, 83%, 47%)',  // red
  'hsl(199, 89%, 48%)',  // cyan
];

export function SourceBreakdown({ data, loading }: SourceBreakdownProps) {
  // Defensive: ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];

  const chartData = useMemo(() => {
    return safeData.slice(0, 6).map((item) => ({
      name: item.source,
      leads: item.leads,
      conversions: item.conversions
    }));
  }, [safeData]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Fontes de Tráfego</CardTitle>
          <CardDescription>Breakdown por UTM source/medium</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Carregando...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (safeData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Fontes de Tráfego</CardTitle>
          <CardDescription>Breakdown por UTM source/medium</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            Nenhum dado disponível
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fontes de Tráfego</CardTitle>
        <CardDescription>Breakdown por UTM source/medium</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Bar Chart */}
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="name"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }}
            />
            <Bar dataKey="leads" name="Leads" radius={[4, 4, 0, 0]}>
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fonte</TableHead>
                <TableHead>Meio</TableHead>
                <TableHead className="text-right">Leads</TableHead>
                <TableHead className="text-right">Conversões</TableHead>
                <TableHead className="text-right">Taxa</TableHead>
                <TableHead className="text-right">Valor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {safeData.map((source, index) => (
                <TableRow key={`${source.source}-${source.medium}-${index}`}>
                  <TableCell className="font-medium">{source.source}</TableCell>
                  <TableCell>{source.medium}</TableCell>
                  <TableCell className="text-right">{source.leads.toLocaleString('pt-BR')}</TableCell>
                  <TableCell className="text-right">{source.conversions.toLocaleString('pt-BR')}</TableCell>
                  <TableCell className="text-right">
                    <span
                      className={
                        source.conversionRate >= 20
                          ? 'text-green-600 font-medium'
                          : source.conversionRate >= 10
                          ? 'text-amber-600'
                          : ''
                      }
                    >
                      {source.conversionRate.toFixed(1)}%
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(source.totalQuoteValue)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
